import {useLoaderData, useNavigate, useOutletContext} from "react-router-dom";
import {ActivityMatchInfo, UserInfo} from "../../interface";
import {EnvironmentOutlined, CalendarOutlined, UserOutlined} from '@ant-design/icons'
import {
    Button,
    Col,
    DatePicker,
    Form,
    Input,
    List,
    message,
    Row,
    Select,
    Switch,
    Typography
} from "antd";
import {useEffect, useState} from "react";
import {regionList} from "../../regionData";
import {getMatchActivitySelfList} from "../../apiBridge";
import { useOrganization } from "../organization/OrganizationContext";
const { Title, Link, Text } = Typography;

const Activities: React.FC = () => {
    const user = useOutletContext() as UserInfo;
    const data = useLoaderData() as {"activities"?: ActivityMatchInfo[], "error"?: string} | null
    const initialActivities = data?.activities || [];
    const { subscriptionFeatures } = useOrganization();

    const [form] = Form.useForm();
    const navigate = useNavigate()

    const [messageApi, contextHolder] = message.useMessage();
    const [loadingState, setLoadingState] = useState(false)
    const [originItems, setOriginItems] = useState<ActivityMatchInfo[]>(initialActivities);
    const [filterItems, setFilterItems] = useState<ActivityMatchInfo[]>(initialActivities);
    const [hasPermission, setHasPermission] = useState(false);

    useEffect(() => {
        if (data?.error) {
            messageApi.warning(data.error);
        }
    }, [data?.error, messageApi]);

    useEffect(() => {
        for (let i = 0; i < subscriptionFeatures.length; i++) {
            if (subscriptionFeatures[i].name === "tournament_mode") {
                setHasPermission(true);
            }
        }
    }, [subscriptionFeatures])

    const fetchActivities = async (all: boolean) => {
        try {
            setLoadingState(true);
            const res = await getMatchActivitySelfList("", all);
            if (res.status === 200) {
                const data = await res.json();
                setOriginItems(data.activities || []);
                setFilterItems(data.activities || []);
            } else {
                let error = 'Failed to fetch activities';
                try {
                    const data = await res.json();
                    if (data?.error) {
                        error = data.error;
                    }
                } catch {
                    // Keep the default message when the response body is not JSON.
                }
                messageApi.error(error);
            }
        } catch (error) {
            messageApi.error('Error fetching activities');
        } finally {
            setLoadingState(false);
        }
    };


    const handlePastChange = (checked: boolean) => {
        fetchActivities(checked);
    };

    const doSearch = (values: any) => {
        console.info("doSearch", values);
        const formValues = values || form.getFieldsValue();
        const { keyword, start_date, end_date, location } = formValues;

        const filteredItems = originItems.filter(item => {
            if (keyword && !item.name.toLowerCase().includes(keyword.toLowerCase())) {
                return false;
            }

            if (location && item.options?.location !== location) {
                return false;
            }

            if (start_date || end_date) {
                const itemStartDate = new Date(item.start_at);
                const itemEndDate = new Date(item.end_at);
                
                if (start_date && itemStartDate < start_date) {
                    return false;
                }
                
                if (end_date && itemEndDate > end_date) {
                    return false;
                }
            }

            return true;
        });

        setFilterItems(filteredItems);
    }

    const toCreate = () => {
        navigate( "/activity/create")
    }

    const toEdit = (value: ActivityMatchInfo)=> {
        console.info("toEdit", value);
        navigate("/activity/" + value.id + "/info");
    }

    return (
        <>
            {contextHolder}

            <div style={{ background: "white", borderRadius: 4, boxShadow: "0 0 3px 0 #cbcbcb", padding: 20, marginBottom: 20 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Col>
                        <Title level={4}>Activities</Title>
                    </Col>
                </Row>

                <Form 
                    initialValues={{keyword: "", start_date: null, end_date: null, location: "", past: true}} 
                    layout="vertical" 
                    form={form} 
                    name="searchForm" 
                    onFinish={doSearch}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            doSearch(null);
                        }
                    }}
                >
                    <Row gutter={24}>
                        <Col sm={6}>
                            <Form.Item label="Search Events" name="keyword">
                                <Input allowClear={true} />
                            </Form.Item>
                        </Col>

                        <Col sm={6}>
                            <Form.Item label="Start Date" name="start_date">
                                <DatePicker style={{width:"100%"}} />
                            </Form.Item>
                        </Col>

                        <Col sm={6}>
                            <Form.Item label="End Date" name="end_date">
                                <DatePicker style={{width:"100%"}} />
                            </Form.Item>
                        </Col>

                        <Col sm={6}>
                            <Form.Item label="Location" name="location">
                                <Select>
                                    <Select.Option key="" value="">Any</Select.Option>
                                    {regionList.map((region) => (
                                        <Select.Option key={region} value={region}>
                                            {region}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col>
                            <Form.Item label="Past" name="past">
                                <Switch onChange={handlePastChange} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Button type="primary" onClick={() => doSearch(null)} loading={loadingState}>Search</Button>
                        
                        {hasPermission && user.role === "Owner" && (
                                <Button type="link" onClick={toCreate}>
                                    Create
                                </Button>
                        )}
                    </Form.Item>
                </Form>
            </div>

            <List
                grid={{
                    gutter: 16,
                    xs: 1,
                    sm: 2,
                    md: 4,
                    lg: 4,
                    xl: 4,
                    xxl: 3,
                }}
                dataSource={filterItems}
                renderItem={(item: ActivityMatchInfo) => (
                    <List.Item>
                        <div style={{ background: "white", borderRadius: 4, boxShadow: "0 0 3px 0 #cbcbcb", padding: 20 }}>
                            {
                                user.role === "Owner" ? <>
                                    <Link onClick={() => toEdit(item)}>Edit</Link>
                                </> : <></>
                            }

                            <Title level={4}><a href={"/web/activity/" + item.id + "/info"}>{item.name}</a></Title>

                            <Text><EnvironmentOutlined /> {item.options?.location}</Text><br />
                            <Text><CalendarOutlined /> {item.start_at} ~ {item.end_at}</Text><br />
                            <Text><UserOutlined /> {item.participant_count} Going</Text>
                        </div>
                    </List.Item>
                )}
            />
        </>
    )
};

export default Activities
